import { Check, Minus } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useRef, type InputHTMLAttributes } from "react";

type CircularCheckboxIndicatorProps = {
  checked: boolean;
  indeterminate?: boolean;
};

export function CircularCheckboxIndicator({ checked, indeterminate = false }: CircularCheckboxIndicatorProps) {
  const active = checked || indeterminate;

  return (
    <span
      className={[
        "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2",
        active ? "border-client-primary bg-client-primary" : "border-gray-300 bg-white",
      ].join(" ")}
      aria-hidden
    >
      {checked ? (
        <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
      ) : indeterminate ? (
        <Minus className="h-2.5 w-2.5 text-white" strokeWidth={3} />
      ) : null}
    </span>
  );
}

export type CircularCheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  indeterminate?: boolean;
};

export const CircularCheckbox = forwardRef<HTMLInputElement, CircularCheckboxProps>(
  function CircularCheckbox(
    { checked, indeterminate = false, className = "", onChange, ...props },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    return (
      <label className={`inline-flex cursor-pointer items-center justify-center ${className}`}>
        <input
          ref={inputRef}
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={onChange}
          {...props}
        />
        <CircularCheckboxIndicator checked={!!checked} indeterminate={indeterminate} />
      </label>
    );
  },
);
